// frontend/src/EducationDashboardDemo.js
// 教育ダッシュボードのデモページ。
// 全パネルにデモデータを流し込んで、機能を体験できる。

import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import dayjs from "dayjs";
import { ArrowLeft, FlaskConical } from "lucide-react";
import Header from "./components/ui/Header";
import StaffLevelProgressPanel from "./components/StaffLevelProgressPanel";
import LevelOneStatusPanel from "./components/LevelOneStatusPanel";

// =============================================================================
// デモデータ
// =============================================================================
const DEMO_LEVELS = [
  { id: 'lv1', name: '★1 (新人)', color: '#16a34a', display_order: 1 },
  { id: 'lv2', name: '★2 (中堅)', color: '#f59e0b', display_order: 2 },
  { id: 'lv3', name: '★3 (上級)', color: '#dc2626', display_order: 3 },
];

const DEMO_PROCEDURES = [
  { id: 'p1', name: '虫垂切除術', department_id: 'dept2', level_id: 'lv1' },
  { id: 'p2', name: '胆嚢摘出術', department_id: 'dept2', level_id: 'lv1' },
  { id: 'p3', name: '鼠径ヘルニア修復術', department_id: 'dept2', level_id: 'lv1' },
  { id: 'p4', name: '人工骨頭置換術', department_id: 'dept3', level_id: 'lv2' },
  { id: 'p5', name: '胃癌手術', department_id: 'dept2', level_id: 'lv2' },
  { id: 'p6', name: '心臓弁置換術', department_id: 'dept1', level_id: 'lv3' },
  { id: 'p7', name: '冠動脈バイパス術', department_id: 'dept1', level_id: 'lv3' },
];

const DEMO_STAFF = [
  { id: 's1', employee_id: '2024001', last_name: '田中', first_name: '太郎', year: 2024 },
  { id: 's2', employee_id: '2024002', last_name: '佐藤', first_name: '花子', year: 2024 },
  { id: 's3', employee_id: '2023001', last_name: '鈴木', first_name: '一郎', year: 2023 },
  { id: 's4', employee_id: '2022001', last_name: '高橋', first_name: '美穂', year: 2022 },
  { id: 's5', employee_id: '2020001', last_name: '山田', first_name: '健太', year: 2020 },
  { id: 's6', employee_id: '2024003', last_name: '伊藤', first_name: 'さくら', year: 2024 },
];

const DEMO_INDEPENDENCE = [
  { id: 'i1',  staff_id: 's1', procedure_id: 'p1', role: 'scrub', recognized_date: dayjs().subtract(5, 'month').format('YYYY-MM-DD') },
  { id: 'i2',  staff_id: 's1', procedure_id: 'p2', role: 'scrub', recognized_date: dayjs().subtract(3, 'month').format('YYYY-MM-DD') },
  { id: 'i3',  staff_id: 's2', procedure_id: 'p1', role: 'scrub', recognized_date: dayjs().subtract(2, 'month').format('YYYY-MM-DD') },
  { id: 'i4',  staff_id: 's3', procedure_id: 'p1', role: 'scrub', recognized_date: dayjs().subtract(10, 'month').format('YYYY-MM-DD') },
  { id: 'i5',  staff_id: 's3', procedure_id: 'p2', role: 'scrub', recognized_date: dayjs().subtract(9, 'month').format('YYYY-MM-DD') },
  { id: 'i6',  staff_id: 's3', procedure_id: 'p3', role: 'scrub', recognized_date: dayjs().subtract(7, 'month').format('YYYY-MM-DD') },
  { id: 'i7',  staff_id: 's3', procedure_id: 'p5', role: 'scrub', recognized_date: dayjs().subtract(2, 'month').format('YYYY-MM-DD') },
  { id: 'i8',  staff_id: 's4', procedure_id: 'p1', role: 'scrub', recognized_date: dayjs().subtract(20, 'month').format('YYYY-MM-DD') },
  { id: 'i9',  staff_id: 's4', procedure_id: 'p2', role: 'scrub', recognized_date: dayjs().subtract(18, 'month').format('YYYY-MM-DD') },
  { id: 'i10', staff_id: 's4', procedure_id: 'p3', role: 'scrub', recognized_date: dayjs().subtract(15, 'month').format('YYYY-MM-DD') },
  { id: 'i11', staff_id: 's4', procedure_id: 'p4', role: 'scrub', recognized_date: dayjs().subtract(10, 'month').format('YYYY-MM-DD') },
  { id: 'i12', staff_id: 's4', procedure_id: 'p5', role: 'scrub', recognized_date: dayjs().subtract(8, 'month').format('YYYY-MM-DD') },
  { id: 'i13', staff_id: 's5', procedure_id: 'p1', role: 'scrub', recognized_date: dayjs().subtract(40, 'month').format('YYYY-MM-DD') },
  { id: 'i14', staff_id: 's5', procedure_id: 'p2', role: 'scrub', recognized_date: dayjs().subtract(38, 'month').format('YYYY-MM-DD') },
  { id: 'i15', staff_id: 's5', procedure_id: 'p3', role: 'scrub', recognized_date: dayjs().subtract(35, 'month').format('YYYY-MM-DD') },
  { id: 'i16', staff_id: 's5', procedure_id: 'p4', role: 'scrub', recognized_date: dayjs().subtract(30, 'month').format('YYYY-MM-DD') },
  { id: 'i17', staff_id: 's5', procedure_id: 'p5', role: 'scrub', recognized_date: dayjs().subtract(28, 'month').format('YYYY-MM-DD') },
  { id: 'i18', staff_id: 's5', procedure_id: 'p6', role: 'scrub', recognized_date: dayjs().subtract(20, 'month').format('YYYY-MM-DD') },
  { id: 'i19', staff_id: 's5', procedure_id: 'p7', role: 'scrub', recognized_date: dayjs().subtract(15, 'month').format('YYYY-MM-DD') },
];

const DEMO_INSTRUCTION_RANKING_DEPT = [
  { key: 'dept2', label: '消化器外科', count: 48 },
  { key: 'dept1', label: '心臓血管外科', count: 31 },
  { key: 'dept3', label: '整形外科', count: 22 },
];
const DEMO_INSTRUCTOR_RANKING = [
  { key: 's5', label: '山田 健太', count: 38 },
  { key: 's4', label: '高橋 美穂', count: 27 },
  { key: 's3', label: '鈴木 一郎', count: 18 },
];
const DEMO_INSTRUCTOR_EFFECT = [
  { name: '山田 健太', total: 38, achieved: 24, avg: 1.8, b1: 12, b2: 8, b3: 3, b4: 1 },
  { name: '高橋 美穂', total: 27, achieved: 16, avg: 2.4, b1: 4,  b2: 7, b3: 4, b4: 1 },
  { name: '鈴木 一郎', total: 18, achieved: 9,  avg: 3.2, b1: 1,  b2: 3, b3: 4, b4: 1 },
];

// =============================================================================
// デモ用簡易ランキング表示 (本番 InstructionRankingPanel は currentOrganization 必須なため)
// =============================================================================
const DemoRankingMini = ({ items, suffix = '回' }) => {
  const top = items[0]?.count || 1;
  return (
    <div>
      {items.map((it, i) => (
        <div key={it.key} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, marginBottom: 6 }}>
          <div style={{ width: 22, fontWeight: 700,
            color: i === 0 ? '#dc2626' : i === 1 ? '#ea580c' : '#64748b' }}>{i + 1}</div>
          <div style={{ flex: 1, fontWeight: 600 }}>{it.label}</div>
          <div style={{ flex: 2, height: 6, background: '#f1f5f9', borderRadius: 3, overflow: 'hidden' }}>
            <div style={{ width: `${(it.count / top) * 100}%`, height: '100%',
              background: 'linear-gradient(90deg, #3b82f6, #1d4ed8)' }} />
          </div>
          <div style={{ width: 50, textAlign: 'right', fontWeight: 700 }}>{it.count} {suffix}</div>
        </div>
      ))}
    </div>
  );
};

const DemoInstructorEffect = () => (
  <div style={{
    background: 'white', borderRadius: 12, padding: 20, border: '1px solid #e5e7eb',
  }}>
    <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 10 }}>🎓 指導効果分析 (デモ)</div>
    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
      <thead style={{ background: '#f8fafc' }}>
        <tr>
          <th style={th}>順位</th>
          <th style={{ ...th, textAlign: 'left' }}>指導者</th>
          <th style={th}>総指導</th>
          <th style={th}>自立達成</th>
          <th style={th}>平均何回目</th>
          <th style={th}>初回</th>
          <th style={th}>2回目</th>
          <th style={th}>3回目</th>
          <th style={th}>4回+</th>
        </tr>
      </thead>
      <tbody>
        {DEMO_INSTRUCTOR_EFFECT.map((r, i) => (
          <tr key={r.name} style={{ borderBottom: '1px solid #e5e7eb' }}>
            <td style={td}>{i + 1}</td>
            <td style={{ ...td, textAlign: 'left', fontWeight: 600 }}>{r.name}</td>
            <td style={td}>{r.total}</td>
            <td style={td}>{r.achieved}</td>
            <td style={{ ...td, fontWeight: 700,
              color: r.avg <= 2 ? '#16a34a' : r.avg <= 3 ? '#ca8a04' : '#dc2626' }}>
              {r.avg.toFixed(1)}
            </td>
            <td style={td}>{r.b1}</td>
            <td style={td}>{r.b2}</td>
            <td style={td}>{r.b3}</td>
            <td style={td}>{r.b4}</td>
          </tr>
        ))}
      </tbody>
    </table>
    <div style={{ marginTop: 10, fontSize: 11, color: '#94a3b8' }}>
      💡 平均が低いほど被指導者が早く自立。 山田さんは平均 1.8 回目で自立 → 教え方が上手いかも。
    </div>
  </div>
);

const EducationDashboardDemo = () => {
  const [rankView, setRankView] = useState('department');
  const demoData = useMemo(() => ({
    staffList: DEMO_STAFF,
    procedures: DEMO_PROCEDURES,
    levels: DEMO_LEVELS,
    independence: DEMO_INDEPENDENCE,
  }), []);

  const rankItems = rankView === 'department'
    ? DEMO_INSTRUCTION_RANKING_DEPT
    : DEMO_INSTRUCTOR_RANKING;

  return (
    <div style={styles.container}>
      <Header />
      <div style={styles.content}>
        <div style={{
          background: 'linear-gradient(135deg, #7c3aed, #6d28d9)', color: 'white',
          padding: 20, borderRadius: 12, marginBottom: 16,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
            <FlaskConical size={20} />
            <h1 style={{ margin: 0, fontSize: 20 }}>教育ダッシュボード - デモモード</h1>
          </div>
          <p style={{ margin: 0, fontSize: 13, opacity: 0.9 }}>
            実データを使わず、サンプルデータで全パネルを体験できます。
            実際にレベル/術式/スタッフ/自立認定を登録すると、本番ダッシュボードで同じ表示になります。
          </p>
          <Link to="/education-dashboard" style={{
            display: 'inline-flex', marginTop: 10, alignItems: 'center', gap: 6,
            padding: '6px 14px', background: 'rgba(255,255,255,0.2)',
            color: 'white', textDecoration: 'none',
            borderRadius: 8, fontSize: 12, fontWeight: 600,
          }}>
            <ArrowLeft size={12} /> 本番ダッシュボードへ
          </Link>
        </div>

        <div style={styles.grid}>
          <LevelOneStatusPanel demoData={demoData} />
          <StaffLevelProgressPanel demoData={demoData} />
          <DemoInstructorEffect />

          {/* 指導回数ランキング (デモ) */}
          <div style={{
            background: 'white', borderRadius: 12, padding: 16, border: '1px solid #e5e7eb',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
              <div style={{ fontSize: 16, fontWeight: 700 }}>📊 指導回数ランキング (デモ)</div>
              <select value={rankView} onChange={(e) => setRankView(e.target.value)} style={{
                padding: '4px 8px', borderRadius: 6, border: '1px solid #d1d5db', fontSize: 12,
              }}>
                <option value="department">診療科別</option>
                <option value="instructor">指導者別</option>
              </select>
            </div>
            <DemoRankingMini items={rankItems} />
          </div>

          {/* 直近の自立認定 (デモ) */}
          <div style={{
            background: 'white', borderRadius: 12, padding: 20, border: '1px solid #e5e7eb',
          }}>
            <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 10 }}>⭐ 直近の自立認定 (デモ)</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {DEMO_INDEPENDENCE.slice().reverse().slice(0, 8).map((r) => {
                const staff = DEMO_STAFF.find((s) => s.id === r.staff_id);
                const proc = DEMO_PROCEDURES.find((p) => p.id === r.procedure_id);
                return (
                  <div key={r.id} style={{
                    display: 'flex', justifyContent: 'space-between',
                    padding: '8px 12px', background: '#f8fafc', borderRadius: 8, fontSize: 13,
                  }}>
                    <div>
                      <strong>{staff?.last_name} {staff?.first_name}</strong> / {proc?.name}
                      <span style={{ marginLeft: 6, fontSize: 11, color: '#64748b' }}>
                        ({r.role === 'scrub' ? '器械出し' : '外回り'})
                      </span>
                    </div>
                    <div style={{ color: '#64748b', fontSize: 12 }}>{r.recognized_date}</div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const styles = {
  container: { minHeight: "100vh", background: "linear-gradient(135deg, #f8fafc, #eff6ff)" },
  content: { maxWidth: "1200px", margin: "0 auto", padding: "24px" },
  grid: { display: "flex", flexDirection: "column", gap: 20 },
};
const th = { padding: 8, textAlign: 'center', fontWeight: 700, color: '#475569', fontSize: 11, border: '1px solid #e5e7eb' };
const td = { padding: 8, textAlign: 'center', fontSize: 12 };

export default EducationDashboardDemo;
